import React, { useMemo, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import ShowChartOutlinedIcon from "@mui/icons-material/ShowChartOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import "./Dashboard_LineChart.css";

/* ------------------------------------------------------------------ */
/* Chart geometry (SVG scales with its container via the viewBox)      */
/* ------------------------------------------------------------------ */
const W = 640;
const H = 300;
const PAD = { top: 26, right: 26, bottom: 58, left: 68 };
const INNER_W = W - PAD.left - PAD.right;
const INNER_H = H - PAD.top - PAD.bottom;
const BASE_Y = PAD.top + INNER_H;
const Y_TICKS = 4;
const DRAW_TIME = 1.4; // seconds for the curve to draw

/* ------------------------------------------------------------------ */
/* Data: chartData = [{ date, order_count }, ...]                      */
/* (same fields the original Chart.js version used)                    */
/* ------------------------------------------------------------------ */
const toNumber = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v ?? "").replace(/[^0-9.-]/g, ""));
  return Number.isFinite(n) ? n : 0;
};

const normalize = (raw) =>
  Array.isArray(raw)
    ? raw.map((item, i) => ({
        label: item?.date ?? String(i + 1),
        value: toNumber(item?.order_count),
      }))
    : [];

const formatLabel = (label) => {
  if (typeof label === "string" && /^\d{4}-\d{2}-\d{2}/.test(label)) {
    const d = new Date(label);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
    }
  }
  return String(label);
};

const formatValue = (n) => n.toLocaleString("en-IN", { maximumFractionDigits: 0 });
const formatAverage = (n) => n.toLocaleString("en-IN", { maximumFractionDigits: 1 });

/* whole-number steps only (same as stepSize: 1 / precision: 0 before) */
const niceStep = (max) => {
  const raw = Math.max(max, 1) / Y_TICKS;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const step = [1, 2, 5, 10].map((m) => m * mag).find((s) => s >= raw) || mag * 10;
  return Math.max(1, step);
};

/* smooth curve that never overshoots (horizontal control handles) */
const buildLinePath = (pts) =>
  pts.reduce((d, p, i) => {
    if (i === 0) return `M ${p.x} ${p.y}`;
    const prev = pts[i - 1];
    const mx = (prev.x + p.x) / 2;
    return `${d} C ${mx} ${prev.y}, ${mx} ${p.y}, ${p.x} ${p.y}`;
  }, "");

/* ------------------------------------------------------------------ */

/**
 * Props:
 *  - chartData: array of { date, order_count } (unchanged contract)
 *  - loading:   optional, shows a skeleton while the request is in flight
 *  - error:     optional, shows an inline error instead of an empty chart
 */
const Dashboard_LineChart = ({ chartData, loading = false, error = false }) => {
  const reduceMotion = useReducedMotion();
  const uid = useRef(`slc-${Math.random().toString(36).slice(2, 8)}`).current;
  const [hover, setHover] = useState(null);

  const model = useMemo(() => {
    const items = normalize(chartData);
    if (!items.length) return null;

    const maxValue = Math.max(...items.map((i) => i.value), 0);
    const step = niceStep(maxValue);
    const top = step * Y_TICKS;
    const n = items.length;

    const points = items.map((item, i) => ({
      ...item,
      x: n === 1 ? PAD.left + INNER_W / 2 : PAD.left + (i * INNER_W) / (n - 1),
      y: BASE_Y - (item.value / top) * INNER_H,
    }));

    const line = buildLinePath(points);
    const area =
      n > 1 ? `${line} L ${points[n - 1].x} ${BASE_Y} L ${points[0].x} ${BASE_Y} Z` : "";

    const ticks = Array.from({ length: Y_TICKS + 1 }, (_, i) => ({
      value: step * i,
      y: BASE_Y - (i / Y_TICKS) * INNER_H,
    }));

    /* summary figures derived from the same order_count values */
    const totalOrders = items.reduce((sum, i) => sum + i.value, 0);
    const peak = items.reduce((best, i) => (i.value > best.value ? i : best), items[0]);

    return {
      points,
      line,
      area,
      ticks,
      labelEvery: Math.ceil(n / 7),
      signature: items.map((i) => `${i.label}:${i.value}`).join(","),
      summary: {
        totalOrders,
        average: totalOrders / n,
        peak,
        days: n,
      },
    };
  }, [chartData]);

  const dur = (s) => (reduceMotion ? 0 : s);

  /* ---------- loading state ---------- */
  if (loading) {
    return (
      <div className="slc slc--loading" role="status" aria-live="polite">
        <div className="slc__skeleton">
          {Array.from({ length: 12 }, (_, i) => (
            <span
              key={i}
              className="slc__skeleton-bar"
              style={{ height: `${30 + ((i * 37) % 55)}%` }}
            />
          ))}
        </div>
        <span className="visually-hidden">Loading order data</span>
      </div>
    );
  }

  /* ---------- error state ---------- */
  if (error) {
    return (
      <div className="slc slc--empty" role="alert">
        <ErrorOutlineIcon className="slc__state-icon slc__state-icon--error" />
        <p className="slc__empty-text">Couldn&apos;t load order data. Please refresh to try again.</p>
      </div>
    );
  }

  /* ---------- empty state ---------- */
  if (!model) {
    return (
      <div className="slc slc--empty">
        <ShowChartOutlinedIcon className="slc__state-icon" />
        <p className="slc__empty-text">No order data for this period yet</p>
      </div>
    );
  }

  const { points, line, area, ticks, labelEvery, signature, summary } = model;
  const n = points.length;
  const last = points[n - 1];
  const showDots = n <= 31;
  const active = hover !== null ? points[hover] : null;

  const pointDelay = (i) =>
    reduceMotion ? 0 : 0.15 + (n === 1 ? 0 : (i / (n - 1)) * DRAW_TIME * 0.85);

  /* tooltip position (clamped inside the chart) */
  const TIP_W = 132;
  const TIP_H = 46;
  const tipX = active ? Math.min(Math.max(active.x - TIP_W / 2, 6), W - TIP_W - 6) : 0;
  const tipY = active ? Math.max(active.y - TIP_H - 14, 4) : 0;

  const stats = [
    { label: "Orders in period", value: formatValue(summary.totalOrders) },
    { label: "Daily average", value: formatAverage(summary.average) },
    {
      label: "Peak day",
      value: formatValue(summary.peak.value),
      hint: formatLabel(summary.peak.label),
    },
  ];

  return (
    <div className="slc">
      <div className="slc__plot">
        {/* key restarts the draw-in whenever the data changes */}
        <motion.svg
          key={signature}
          viewBox={`0 0 ${W} ${H}`}
          className="slc__svg"
          role="img"
          aria-label={`Orders per day for ${summary.days} days, ${formatValue(
            summary.totalOrders
          )} orders in total`}
          onMouseLeave={() => setHover(null)}
        >
          <defs>
            <linearGradient id={`${uid}-stroke`} x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#a67c52" />
              <stop offset="100%" stopColor="#401b13" />
            </linearGradient>
            <linearGradient id={`${uid}-area`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#a67c52" stopOpacity="0.32" />
              <stop offset="100%" stopColor="#a67c52" stopOpacity="0" />
            </linearGradient>
          </defs>

          {/* axis titles (same text as before: Dates / Order Count) */}
          <text
            className="slc__axis-title"
            x={PAD.left + INNER_W / 2}
            y={H - 8}
            textAnchor="middle"
          >
            Dates
          </text>
          <text
            className="slc__axis-title"
            transform={`translate(15 ${PAD.top + INNER_H / 2}) rotate(-90)`}
            textAnchor="middle"
          >
            Order Count
          </text>

          {/* grid + y labels (grid lines wipe in from the left) */}
          {ticks.map((t, i) => (
            <g key={t.value}>
              <motion.line
                x1={PAD.left}
                x2={W - PAD.right}
                y1={t.y}
                y2={t.y}
                className={i === 0 ? "slc__axis" : "slc__grid"}
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: dur(0.6), delay: dur(i * 0.05), ease: "easeOut" }}
              />
              <motion.text
                x={PAD.left - 10}
                y={t.y + 4}
                textAnchor="end"
                className="slc__tick"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: dur(0.4), delay: dur(0.15 + i * 0.05) }}
              >
                {formatValue(t.value)}
              </motion.text>
            </g>
          ))}

          {/* x labels */}
          {points.map(
            (p, i) =>
              i % labelEvery === 0 && (
                <motion.text
                  key={`x-${i}`}
                  x={p.x}
                  y={BASE_Y + 20}
                  textAnchor="middle"
                  className="slc__tick"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: dur(0.4), delay: pointDelay(i) }}
                >
                  {formatLabel(p.label)}
                </motion.text>
              )
          )}

          {/* area under the curve */}
          {area && (
            <motion.path
              d={area}
              fill={`url(#${uid}-area)`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: dur(0.9), delay: dur(DRAW_TIME * 0.5), ease: "easeOut" }}
            />
          )}

          {/* the curve itself: draws from left to right */}
          {n > 1 && (
            <motion.path
              d={line}
              className="slc__line"
              stroke={`url(#${uid}-stroke)`}
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: dur(DRAW_TIME), ease: [0.65, 0, 0.35, 1] }}
            />
          )}

          {/* vertical guide + hover dot */}
          {active && (
            <>
              <motion.line
                className="slc__guide"
                y1={PAD.top}
                y2={BASE_Y}
                initial={{ x1: active.x, x2: active.x, opacity: 0 }}
                animate={{ x1: active.x, x2: active.x, opacity: 1 }}
                transition={{ type: "spring", stiffness: 380, damping: 32 }}
              />
              <motion.circle
                className="slc__dot slc__dot--active"
                initial={{ cx: active.x, cy: active.y, r: 0 }}
                animate={{ cx: active.x, cy: active.y, r: 6.5 }}
                transition={{ type: "spring", stiffness: 380, damping: 28 }}
              />
            </>
          )}

          {/* point dots appear as the line reaches them */}
          {showDots &&
            points.map((p, i) => (
              <motion.circle
                key={`dot-${i}`}
                cx={p.x}
                cy={p.y}
                r={4}
                className="slc__dot"
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: hover === i ? 0 : 1, opacity: hover === i ? 0 : 1 }}
                style={{ transformBox: "fill-box", transformOrigin: "center" }}
                transition={{
                  duration: dur(0.25),
                  ease: "easeOut",
                  delay: hover === null ? pointDelay(i) : 0,
                }}
              />
            ))}

          {/* one soft halo on the latest point once the curve finishes */}
          {!reduceMotion && (
            <motion.circle
              cx={last.x}
              cy={last.y}
              fill="#a67c52"
              initial={{ r: 4, opacity: 0 }}
              animate={{ r: [4, 15], opacity: [0.4, 0] }}
              transition={{
                duration: 1.6,
                delay: DRAW_TIME + 0.2,
                repeat: 2,
                ease: "easeOut",
              }}
            />
          )}

          {/* invisible hover columns */}
          {points.map((p, i) => {
            const colW = n === 1 ? INNER_W : INNER_W / (n - 1);
            return (
              <rect
                key={`hit-${i}`}
                x={p.x - colW / 2}
                y={PAD.top}
                width={colW}
                height={INNER_H}
                className="slc__hit"
                onMouseEnter={() => setHover(i)}
                onMouseMove={() => hover !== i && setHover(i)}
                onTouchStart={() => setHover(i)}
              />
            );
          })}

          {/* tooltip */}
          {active && (
            <motion.g
              style={{ pointerEvents: "none" }}
              initial={{ opacity: 0, x: tipX, y: tipY + 6 }}
              animate={{ opacity: 1, x: tipX, y: tipY }}
              transition={{ type: "spring", stiffness: 380, damping: 30 }}
            >
              <rect width={TIP_W} height={TIP_H} rx="10" className="slc__tip-bg" />
              <text x={TIP_W / 2} y="18" textAnchor="middle" className="slc__tip-label">
                {formatLabel(active.label)}
              </text>
              <text x={TIP_W / 2} y="36" textAnchor="middle" className="slc__tip-value">
                {formatValue(active.value)} {active.value === 1 ? "order" : "orders"}
              </text>
            </motion.g>
          )}
        </motion.svg>
      </div>

      {/* supporting statistics (derived from the same order_count values) */}
      <dl className="slc__stats">
        {stats.map((s, i) => (
          <motion.div
            key={s.label}
            className="slc__stat"
            initial={reduceMotion ? false : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: reduceMotion ? 0 : 0.2 + i * 0.08 }}
          >
            <dt className="slc__stat-label">{s.label}</dt>
            <dd className="slc__stat-value">
              {s.value}
              {s.hint && <span className="slc__stat-hint">{s.hint}</span>}
            </dd>
          </motion.div>
        ))}
      </dl>
    </div>
  );
};

export default Dashboard_LineChart;
